import { useEffect, useState } from "react";
import { demoApi } from "@/api/client";
import type { BillsDue } from "@/api/types";
import { describeReadFailure } from "@/lib/apiFailure";
import { useSelectedEntity } from "@/hooks/useSelectedEntity";

export type BillsDueStatus = "loading" | "ready" | "empty" | "error";

export interface UseBillsDueResult {
  /** Null until the request resolves, or if it failed. */
  billsDue: BillsDue | null;
  /** `empty` is a successful answer where both buckets are zero. */
  status: BillsDueStatus;
  error: string | null;
}

/**
 * ef-061-3 - fetches GET /api/demo/home/bills-due for the entity selected in the chrome. Waits for the entity
 * list before asking (so the request always names an entity), refetches when the selection changes, and aborts a
 * superseded request. Same loading/ready/error shape as `useCashFlowPlanner`, plus `empty` for "nothing owed".
 */
export function useBillsDue(): UseBillsDueResult {
  const { entityId, status: entityStatus } = useSelectedEntity();
  const [billsDue, setBillsDue] = useState<BillsDue | null>(null);
  const [status, setStatus] = useState<BillsDueStatus>("loading");
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (entityStatus === "error") {
      setBillsDue(null);
      setStatus("error");
      setError("Couldn't load your farm entities.");
      return;
    }
    if (entityStatus !== "ready" || entityId === null) {
      setStatus("loading");
      return;
    }

    let cancelled = false;
    const controller = new AbortController();

    async function load(id: string): Promise<void> {
      setStatus("loading");
      setError(null);
      try {
        const result = await demoApi.getBillsDue(id, controller.signal);
        if (cancelled) return;
        setBillsDue(result);
        setStatus(result.due.count === 0 && result.overdue.count === 0 ? "empty" : "ready");
      } catch (err) {
        if (cancelled || (err instanceof DOMException && err.name === "AbortError")) return;
        setBillsDue(null);
        setStatus("error");
        setError(describeReadFailure(err));
      }
    }

    void load(entityId);
    return () => {
      cancelled = true;
      controller.abort();
    };
  }, [entityId, entityStatus]);

  return { billsDue, status, error };
}
